// libs
import { closeSnackbar, enqueueSnackbar } from 'notistack'
// types
import { AppError, AppErrorKind } from 'types/errors'
// providers
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'
// utils
import { getUserMessage, isRequestError, normalizeError } from 'utils/errors/normalize'

// console.error survives the production build (console.log / console.info are stripped)
export const reportError = (error: AppError, source: string) => {
  console.error(`[${source}]`, error.kind, error.codes.join(', '), error.original)
}

export const notifyError = (error: AppError) => {
  // The offline banner (NetworkStatus) already explains what is going on
  if (error.kind === AppErrorKind.NETWORK && typeof navigator !== 'undefined' && !navigator.onLine) {
    return
  }

  enqueueSnackbar(getUserMessage(error), {
    variant: 'error',
    // Stays until the user dismisses it, like the other error snackbars in the app
    autoHideDuration: null,
    action: (key) => getSnackbarCloseButton(key, closeSnackbar)
  })
}

// For catch blocks that have nothing specific to do with the error.
// Request errors are already logged (and, for mutations, shown) by the Apollo ErrorLink;
// anything else thrown inside the try would otherwise disappear without a trace.
// `notify` is for a try that does more than the request (reading a file, etc.): such a failure
// never reaches the ErrorLink, so the user is told here.
export const handleError = (error: unknown, source: string, { notify = false }: { notify?: boolean } = {}) => {
  const appError = normalizeError(error)

  if (isRequestError(error) || appError.kind === AppErrorKind.NETWORK) {
    return
  }

  reportError(appError, source)

  if (notify) {
    notifyError(appError)
  }
}
